import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const siteUrl = typeof __VITE_SITE_URL__ !== 'undefined' ? __VITE_SITE_URL__ : 'https://cssspreadsheet.monster';

const guides = [
  {
    slug: '/cssbuy-spreadsheet-guide',
    image: 'https://readdy.ai/api/search-image?query=Detailed%20closeup%20of%20a%20person%20holding%20a%20tablet%20with%20a%20spreadsheet%20interface%20visible%2C%20a%20pair%20of%20white%20sneakers%20placed%20on%20a%20wooden%20desk%20beside%20a%20steaming%20coffee%20mug%2C%20warm%20morning%20light%20from%20a%20window%20creating%20cozy%20atmosphere%2C%20modern%20productive%20workspace%20with%20fashion%20elements%2C%20editorial%20lifestyle%20photography%20with%20soft%20focus%20on%20peripheral%20items&width=600&height=400&seq=shop-guide-card-01&orientation=landscape',
    title: 'The Ultimate CSSBuy Spreadsheet Guide',
    description: 'Everything you need to know about using CSSBuy spreadsheets — from navigating categories and reading QC photos to comparing agents and spotting trending items. The complete walkthrough for beginners and experienced buyers alike.',
    label: 'Complete Guide',
  },
  {
    slug: '/cssbuy-qc-tutorial',
    image: 'https://readdy.ai/api/search-image?query=Professional%20quality%20inspection%20scene%20with%20a%20pair%20of%20designer%20sneakers%20being%20examined%20under%20a%20bright%20magnifying%20desk%20lamp%2C%20measuring%20tape%20and%20inspection%20checklist%20clipboard%20on%20the%20side%2C%20clean%20white%20workbench%20surface%2C%20crisp%20focused%20lighting%20highlighting%20stitching%20details%20and%20material%20texture%2C%20industrial%20quality%20control%20photography%20with%20clinical%20precision%20and%20sharp%20detail&width=600&height=400&seq=shop-guide-card-02&orientation=landscape',
    title: 'CSSBuy QC Tutorial: Master Quality Control',
    description: 'Learn how to analyze QC photos like a pro. This guide breaks down every detail you need to check — stitching, logos, colors, materials, and proportions — so you never get stuck with a bad batch again.',
    label: 'QC Tutorial',
  },
  {
    slug: '/cssbuy-best-finds-2026',
    image: 'https://readdy.ai/api/search-image?query=Curated%20collection%20of%20trending%20fashion%20items%20arranged%20on%20a%20warm%20terracotta%20surface%20including%20the%20latest%20sneakers%20a%20minimalist%20watch%20a%20leather%20crossbody%20bag%20and%20layered%20gold%20necklaces%2C%20soft%20afternoon%20sunlight%20casting%20geometric%20window%20shadow%20patterns%20across%20the%20display%2C%20fashion%20editorial%20styling%20with%20hand%20written%20trend%20notes%20on%20kraft%20paper%20cards%20placed%20beside%20each%20item%2C%20creative%20director%20mood%20board%20aesthetic&width=600&height=400&seq=shop-guide-card-03&orientation=landscape',
    title: 'Best CSSBuy Spreadsheet Finds of 2026',
    description: 'A handpicked selection of the most popular and highly rated items found in the CSSBuy spreadsheet this year. Sneakers, streetwear, luxury accessories, and hidden gems that the community cannot stop talking about.',
    label: 'Best Finds',
  },
];

export default function ShoppingGuidePage() {
  const navigate = useNavigate();

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-24 md:pt-28 pb-10 md:pb-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <button
                  onClick={() => {
                    navigate('/');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="inline-flex items-center gap-1 text-xs font-medium text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Home
                </button>
                <span className="text-foreground-300">/</span>
                <span className="text-xs text-foreground-400 whitespace-nowrap">Shopping Guide</span>
              </div>
              <span className="inline-block px-3 py-1 text-xs font-semibold text-accent-700 bg-accent-100 rounded-full mb-4 whitespace-nowrap">
                Curated Guides
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground-950 leading-tight mb-4">
                CSSBuy Shopping Guide Hub
              </h1>
              <p className="text-sm text-foreground-500 leading-relaxed max-w-lg mx-auto">
                Your one-stop resource for mastering the CSSBuy spreadsheet. Browse our collection of expert-written guides covering QC, best finds, agent comparisons, and more.
              </p>
            </div>
          </div>
        </section>

        {/* Guide Cards */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto space-y-6">
              {guides.map((guide) => (
                <div
                  key={guide.slug}
                  className="group flex flex-col md:flex-row gap-5 bg-background-100 rounded-2xl overflow-hidden border border-background-200/70 hover:border-accent-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-full md:w-56 h-48 md:h-auto flex-shrink-0 overflow-hidden">
                    <img
                      src={guide.image}
                      alt={guide.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex-1 p-5 md:p-6 flex flex-col justify-center">
                    <span className="inline-block px-2.5 py-0.5 text-xs font-semibold text-accent-700 bg-accent-100 rounded-full mb-2 self-start whitespace-nowrap">
                      {guide.label}
                    </span>
                    <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-2 group-hover:text-accent-700 transition-colors">
                      {guide.title}
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-3">
                      {guide.description}
                    </p>
                    <button
                      onClick={() => {
                        navigate(guide.slug);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap self-start"
                    >
                      Read Full Guide
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Internal Links + CTA */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="bg-background-50 rounded-2xl p-8 md:p-10 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-accent-100 mb-5">
                  <i className="ri-file-list-3-line text-accent-600 text-2xl"></i>
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
                  Ready to Start Exploring?
                </h2>
                <p className="text-sm text-foreground-500 max-w-md mx-auto mb-6">
                  Jump directly into the full CSSBuy spreadsheet database at kaspreadsheet.com — over 1,000 verified items across 14 categories, updated daily.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-7 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Explore kaspreadsheet
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                  <button
                    onClick={() => {
                      navigate('/');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="inline-flex items-center gap-2 px-7 py-3 text-sm font-medium text-foreground-700 border border-background-300 hover:bg-background-100 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Back to Homepage
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              'name': 'CSSBuy Shopping Guide Hub',
              'description': 'Your one-stop resource for mastering the CSSBuy spreadsheet. Browse our collection of expert-written guides covering QC, best finds, agent comparisons, and more.',
              'url': siteUrl + '/cssbuy-shopping-guide',
              'hasPart': [
                {
                  '@type': 'Article',
                  'headline': 'The Ultimate CSSBuy Spreadsheet Guide',
                  'url': siteUrl + '/cssbuy-spreadsheet-guide',
                },
                {
                  '@type': 'Article',
                  'headline': 'CSSBuy QC Tutorial: Master Quality Control',
                  'url': siteUrl + '/cssbuy-qc-tutorial',
                },
                {
                  '@type': 'Article',
                  'headline': "Best CSSBuy Spreadsheet Finds of 2026",
                  'url': siteUrl + '/cssbuy-best-finds-2026',
                },
              ],
              'publisher': {
                '@type': 'Organization',
                'name': 'CSSSpreadsheet',
                'url': siteUrl,
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}